import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProductImageUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Store the absolute URL so <img src> works everywhere
      const absoluteUrl = `${BACKEND}${data.url}`;
      onChange(absoluteUrl);
      toast.success("Image uploaded");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div className="mt-2" data-testid="product-image-uploader">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        data-testid="product-image-input"
      />
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative flex items-center gap-4 p-4 rounded-2xl border border-dashed transition-colors cursor-pointer select-none ${
          dragOver ? "border-oxblood bg-white" : "border-borderSubtle bg-white hover:border-charcoal"
        } ${busy ? "opacity-70 cursor-wait" : ""}`}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="Product"
              className="w-24 h-24 object-cover rounded-xl border border-borderSubtle bg-bone shrink-0"
              data-testid="product-image-preview"
              onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-1 truncate">Image uploaded</p>
              <p className="text-[11px] text-stone font-mono truncate">{value.split("/").pop()}</p>
              <p className="text-[11px] text-stone mt-2">Click to replace, or drop a new file here.</p>
            </div>
            <button
              type="button"
              onClick={clear}
              className="shrink-0 rounded-full p-2 text-stone hover:text-oxblood hover:bg-bone transition-colors"
              data-testid="product-image-remove"
              aria-label="Remove image"
            >
              <X size={16}/>
            </button>
          </>
        ) : (
          <>
            <div className="w-24 h-24 rounded-xl bg-bone border border-borderSubtle flex items-center justify-center shrink-0">
              {busy ? <Loader2 size={22} strokeWidth={1.5} className="animate-spin text-stone"/> :
                     <ImageIcon size={22} strokeWidth={1.5} className="text-stone"/>}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 inline-flex items-center gap-2">
                <Upload size={14} strokeWidth={2}/> {busy ? "Uploading…" : "Drag & drop or click to upload"}
              </p>
              <p className="text-[11px] text-stone">JPG, PNG, WEBP, or GIF · up to 5 MB</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
